import React, { useState, useEffect } from 'react';
import { Upload, Sparkles, FileText, Edit, Trash, CheckCircle2, Plus, X, Paperclip, Save, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import MCQGenerator from './MCQGenerator';
import AIApprovalModal from './AIApprovalModal';
import AddQuestionModal from './AddQuestionModal';
import AIGenerationModal from './AIGenerationModal';
import { parseFile, getSampleCSVFormat, getSampleJSONFormat } from '../utils/questionParser';
import { getAccessToken } from '../../../utils/tokenUtils';
import { listCOsByCourse } from '../../../services/program.service';
import {
  card,
  surfaceMuted,
  emptyState,
  cardTitle,
  bodySecondary,
  badgeBase,
  dividerInner,
  iconSize,
  motionFadeUp,
  motionStaggerChild,
} from '../../../lib/design-system';
import { cn } from '../../../lib/utils';

const inputClasses = "w-full rounded-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-accent1/50 focus:border-transparent p-3 text-sm";
const btnPrimary = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm bg-accent1 text-white text-sm font-medium hover:bg-accent1/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";
const btnSecondary = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-sm border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

const QuestionsStep = ({
  questions, setQuestions,
  activeTab, setActiveTab,
  courseData,
  assignmentType = null, // 'subjective' or 'objective' - if set, restricts to that type only
  attachments = [], // Assignment-level attachments
  setAttachments = () => {}, // Function to update assignment-level attachments
  totalPoints = 100, // Total points for the assignment
  isUngraded = false // Whether the assignment is ungraded
}) => {
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showMCQGenerator, setShowMCQGenerator] = useState(false);
  const [showAIApproval, setShowAIApproval] = useState(false);
  const [pendingAIQuestions, setPendingAIQuestions] = useState([]);

  // Modal states
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);
  const [showAIGenerationModal, setShowAIGenerationModal] = useState(false);
  const [showAIGenerationModalMCQ, setShowAIGenerationModalMCQ] = useState(false);

  // AI Generator State (for subjective)
  const [numQuestions, setNumQuestions] = useState(5);
  const [selectedBloomLevel, setSelectedBloomLevel] = useState('');
  const [selectedModule2, setSelectedModule2] = useState('');
  const [selectedCourseOutcome, setSelectedCourseOutcome] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState(null);

  // Editing state
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editQuestionText, setEditQuestionText] = useState('');
  const [editPoints, setEditPoints] = useState(10);
  const [editBloomLevel, setEditBloomLevel] = useState('');
  const [editCourseOutcome, setEditCourseOutcome] = useState('');

  // Tab state for Questions step
  const [questionsTab, setQuestionsTab] = useState('create'); // 'create' or 'attachments'

  // V2 course outcomes fetched from API — preferred source of truth over legacy learningOutcomes
  const [v2Outcomes, setV2Outcomes] = useState([]);
  useEffect(() => {
    const courseId = courseData?._id || courseData?.id;
    if (!courseId) return;
    listCOsByCourse(courseId)
      .then((data) => {
        const outcomes = data?.outcomes || data || [];
        if (Array.isArray(outcomes) && outcomes.length > 0) {
          setV2Outcomes(outcomes);
        }
      })
      .catch(() => {
        // silently fall back to legacy — getCourseOutcomes handles the fallback
      });
  }, [courseData?._id, courseData?.id]);

  const bloomLevels = ['Remember', 'Understand', 'Apply', 'Analyze', 'Evaluate', 'Create'];

  // Given a CO code string (e.g. "CO1"), return the matching V2 outcome _id (if available).
  const resolveV2Id = (code) => {
    if (!code || v2Outcomes.length === 0) return undefined;
    const match = v2Outcomes.find((co) => co.code === code);
    return match ? match._id : undefined;
  };

  // Returns COs for dropdowns. Prefers V2 (CourseOutcomeV2 docs), falls back to legacy learningOutcomes.
  // Values are always code strings (CO1, CO2…) for backward compat.
  const getCourseOutcomes = () => {
    if (v2Outcomes.length > 0) {
      return v2Outcomes.map((co) => ({ code: co.code, description: co.statement }));
    }

    if (!courseData) return [];

    if (courseData.courseOutcomes && Array.isArray(courseData.courseOutcomes) && courseData.courseOutcomes.length > 0) {
      return courseData.courseOutcomes;
    }
    if (courseData.course_outcomes && Array.isArray(courseData.course_outcomes) && courseData.course_outcomes.length > 0) {
      return courseData.course_outcomes;
    }
    if (courseData.learningOutcomes && Array.isArray(courseData.learningOutcomes) && courseData.learningOutcomes.length > 0) {
      return courseData.learningOutcomes.map((outcome, index) => {
        const outcomeStr = String(outcome);
        const coMatch = outcomeStr.match(/^(CO\d+)/i);
        const loMatch = outcomeStr.match(/^(LO\d+)/i);
        if (coMatch) {
          return { code: coMatch[1], description: outcomeStr.replace(/^CO\d+[:\s]*/i, '').trim() || outcomeStr };
        } else if (loMatch) {
          return { code: loMatch[1].replace(/^LO/i, 'CO'), description: outcomeStr.replace(/^LO\d+[:\s]*/i, '').trim() || outcomeStr };
        }
        return { code: `CO${index + 1}`, description: outcomeStr };
      });
    }

    return [];
  };

  // Helper function to extract CO code from outcome string
  // Handles formats like: "CO1 (Description)" or "CO1" or { code: 'CO1', description: 'Description' }
  const extractOutcomeCode = (outcome) => {
    if (!outcome) return '';

    // If it's an object with code property
    if (typeof outcome === 'object' && outcome.code) {
      return outcome.code;
    }

    // If it's a string, extract CO1, CO2, etc.
    if (typeof outcome === 'string') {
      const match = outcome.match(/^(CO\d+)/i);
      return match ? match[1] : outcome;
    }

    return outcome;
  };

  // Helper function to format outcome for display
  const formatOutcomeForDisplay = (outcome) => {
    if (!outcome) return '';

    // If it's an object with code and description
    if (typeof outcome === 'object' && outcome.code) {
      return outcome.description
        ? `${outcome.code} (${outcome.description})`
        : outcome.code;
    }

    // If it's already a string with format "CO1 (Description)" or just "CO1"
    if (typeof outcome === 'string') {
      return outcome;
    }

    return String(outcome);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);
    try {
      const parsedQuestions = await parseFile(file);
      // If assignmentType is restricted, only add questions of that type
      const validQuestions = assignmentType
        ? parsedQuestions.filter(q => q.type === assignmentType)
        : parsedQuestions;

      if (assignmentType && validQuestions.length < parsedQuestions.length) {
        alert(`Only ${assignmentType} questions are allowed. Some questions were filtered out.`);
      }

      // Only validate if assignment is not ungraded
      if (!isUngraded && totalPoints > 0) {
        const currentTotal = calculateTotalQuestionPoints();
        const newQuestionsTotal = validQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
        const newTotal = currentTotal + newQuestionsTotal;

        if (newTotal > totalPoints) {
          const remaining = totalPoints - currentTotal;
          if (remaining <= 0) {
            alert(`Cannot add questions from file. Total points already reached (${currentTotal}/${totalPoints}). Please adjust question points or total assignment points.`);
            return;
          }
          const confirm = window.confirm(
            `Warning: Adding questions from file will exceed the total points (${newTotal} > ${totalPoints}).\n\n` +
            `Current total: ${currentTotal} points\n` +
            `File questions total: ${newQuestionsTotal} points\n` +
            `Remaining points available: ${remaining} points\n\n` +
            `Would you like to proceed anyway?`
          );
          if (!confirm) {
            return;
          }
        }
      }

      setQuestions(prev => [...prev, ...validQuestions]);
    } catch (error) {
      alert(`Error parsing file: ${error.message}`);
    }
  };

  const generateAISubjectiveQuestions = async (params) => {
    const { numQuestions: num, selectedCourseOutcome: co, selectedBloomLevel: bloom, additionalContext: context } = params;

    setGenerating(true);
    setAiError(null);

    const apiEndpoint = process.env.REACT_APP_QUESTION_GENERATOR_API_URL;

    try {
      // Extract modules, topics, and units from courseData
      const modules = [];
      const topics = [];
      const units = [];

      if (courseData?.syllabus?.modules && Array.isArray(courseData.syllabus.modules)) {
        courseData.syllabus.modules.forEach((module, moduleIndex) => {
          // Add module identifier (e.g., "M1", "M2")
          modules.push(`M${module.moduleNumber || moduleIndex + 1}`);

          // Add topics for this module
          if (module.topics && Array.isArray(module.topics)) {
            module.topics.forEach((topic, topicIndex) => {
              topics.push(`M${module.moduleNumber || moduleIndex + 1}T${topicIndex + 1}`);
            });
          }
        });
      }

      // Get course code and remove spaces
      const courseCode = (courseData?.courseCode || courseData?.course_code || "").replace(/\s+/g, "");

      // Generate questions - API expects JSON format
      const requestBody = {
        course_code: courseCode,
        selected_cos: [co],
        selected_bloom: bloom.toLowerCase(),
        selected_type: "subjective", // Subjective/Long answer questions
        num_questions: num,
        extra_prompt: context || "",
        modules: modules,
        topics: topics,
        units: units
      };

      const token = getAccessToken();
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error (${response.status}): ${errorText}`);
      }

      const data = await response.json();

      if (data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
        const generatedQuestions = data.questions.map((q_item, index) => {
          // The actual question text is in the 'output' property
          const questionText = q_item.output || q_item.question || '';
          return {
            id: `ai_${Date.now()}_${index}`,
            question: questionText,
            type: 'subjective',
            bloomLevel: q_item.bloom_level || bloom.toLowerCase(),
            courseOutcome: extractOutcomeCode(q_item.co || co),
            options: null,
            correctAnswer: null,
            points: 20, // Default points, can be edited later
            score: 20, // Add score field for per-question scoring
            source: 'ai',
            status: 'pending'
          };
        });

        setShowAIGenerationModal(false);
        setPendingAIQuestions(generatedQuestions);
        setShowAIApproval(true);
      } else {
        throw new Error("No questions were generated in the expected format.");
      }
    } catch (err) {
      console.error("Error generating questions:", err);
      setAiError(err.message || "Failed to generate questions. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const generateAIMCQQuestions = async (params) => {
    const { numQuestions: num, selectedCourseOutcome: co, selectedBloomLevel: bloom, additionalContext: context } = params;

    setGenerating(true);
    setAiError(null);

    const apiEndpoint = process.env.REACT_APP_QUESTION_GENERATOR_API_URL;
    try {
      // Extract modules, topics, and units from courseData
      const modules = [];
      const topics = [];
      const units = [];

      if (courseData?.syllabus?.modules && Array.isArray(courseData.syllabus.modules)) {
        courseData.syllabus.modules.forEach((module, moduleIndex) => {
          // Add module identifier (e.g., "M1", "M2")
          modules.push(`M${module.moduleNumber || moduleIndex + 1}`);

          // Add topics for this module
          if (module.topics && Array.isArray(module.topics)) {
            module.topics.forEach((topic, topicIndex) => {
              topics.push(`M${module.moduleNumber || moduleIndex + 1}T${topicIndex + 1}`);
            });
          }
        });
      }

      // Get course code and remove spaces
      const courseCode = (courseData?.courseCode || courseData?.course_code || "").replace(/\s+/g, "");

      // Generate questions - API expects JSON format
      const requestBody = {
        course_code: courseCode,
        selected_cos: [co],
        selected_bloom: bloom.toLowerCase(),
        selected_type: "objective", // MCQ questions
        num_questions: num,
        extra_prompt: context || "",
        modules: modules,
        topics: topics,
        units: units
      };

      const token = getAccessToken();
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error (${response.status}): ${errorText}`);
      }

      const data = await response.json();

      if (data.questions && Array.isArray(data.questions) && data.questions.length > 0) {
        const generatedQuestions = data.questions.map((q_item, index) => {
          // For MCQ, we need to handle options and correctAnswer
          const questionText = q_item.question || '';
          const options = q_item.options || [];
          const correctAnswer = q_item.correct_answer || q_item.correctAnswer || '';

          return {
            id: `ai_mcq_${Date.now()}_${index}`,
            question: questionText,
            type: 'objective',
            bloomLevel: q_item.bloom_level || bloom.toLowerCase(),
            courseOutcome: extractOutcomeCode(q_item.co || co),
            options: options.length >= 2 ? options : ['', '', '', ''],
            correctAnswer: correctAnswer,
            points: q_item.estimated_points || 5, // Default points for MCQ
            score: q_item.estimated_points || 5, // Add score field for per-question scoring
            source: 'ai',
            status: 'pending'
          };
        });

        setShowAIGenerationModalMCQ(false);
        setPendingAIQuestions(generatedQuestions);
        setShowAIApproval(true);
      } else {
        throw new Error("No questions were generated in the expected format.");
      }
    } catch (err) {
      console.error("Error generating MCQ questions:", err);
      setAiError(err.message || "Failed to generate questions. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const handleApproveAIQuestions = (approvedQuestions) => {
    const approved = approvedQuestions.map(q => ({
      ...q,
      status: 'approved'
    }));

    // If assignmentType is restricted, only add questions of that type
    const validQuestions = assignmentType
      ? approved.filter(q => q.type === assignmentType)
      : approved;

    if (assignmentType && validQuestions.length < approved.length) {
      alert(`Only ${assignmentType} questions are allowed. Some questions were filtered out.`);
    }

    // Only validate if assignment is not ungraded
    if (!isUngraded && totalPoints > 0) {
      const currentTotal = calculateTotalQuestionPoints();
      const newQuestionsTotal = validQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
      const newTotal = currentTotal + newQuestionsTotal;

      if (newTotal > totalPoints) {
        const remaining = totalPoints - currentTotal;
        if (remaining <= 0) {
          alert(`Cannot add questions. Total points already reached (${currentTotal}/${totalPoints}). Please adjust question points or total assignment points.`);
          return;
        }
        const confirm = window.confirm(
          `Warning: Adding these questions will exceed the total points (${newTotal} > ${totalPoints}).\n\n` +
          `Current total: ${currentTotal} points\n` +
          `New questions total: ${newQuestionsTotal} points\n` +
          `Remaining points available: ${remaining} points\n\n` +
          `Would you like to proceed anyway?`
        );
        if (!confirm) {
          return;
        }
      }
    }

    const enriched = validQuestions.map(q => ({
      ...q,
      ...(q.courseOutcome && !q.courseOutcomeId ? { courseOutcomeId: resolveV2Id(q.courseOutcome) } : {}),
    }));
    setQuestions(prev => [...prev, ...enriched]);
    setPendingAIQuestions([]);
  };

  const handleRejectAIQuestions = (rejectedQuestions) => {
    setPendingAIQuestions([]);
  };

  const removeQuestion = (id) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };


  // Calculate total points from all questions (use score if available, fallback to points)
  const calculateTotalQuestionPoints = () => {
    return questions.reduce((sum, q) => sum + (q.score || q.points || 0), 0);
  };

  const handleAddManualQuestion = (newQuestion) => {
    // Only validate if assignment is not ungraded
    if (!isUngraded && totalPoints > 0) {
      const currentTotal = calculateTotalQuestionPoints();
      const newTotal = currentTotal + (newQuestion.points || 0);

      if (newTotal > totalPoints) {
        const remaining = totalPoints - currentTotal;
        if (remaining <= 0) {
          alert(`Cannot add question. Total points already reached (${currentTotal}/${totalPoints}). Please adjust question points or total assignment points.`);
          return;
        }
        const confirm = window.confirm(
          `Warning: Adding this question will exceed the total points (${newTotal} > ${totalPoints}).\n\n` +
          `Current total: ${currentTotal} points\n` +
          `This question: ${newQuestion.points} points\n` +
          `Remaining points available: ${remaining} points\n\n` +
          `Would you like to proceed anyway?`
        );
        if (!confirm) {
          return;
        }
      } else if (newTotal < totalPoints) {
        const remaining = totalPoints - newTotal;
        // Just show a warning, but allow it
        console.log(`Points total: ${newTotal}/${totalPoints}. Remaining: ${remaining} points.`);
      }
    }

    setQuestions(prev => [...prev, newQuestion]);
  };

  const handleStartEdit = (question) => {
    setEditingQuestionId(question.id);
    setEditQuestionText(question.question);
    // For ungraded assignments, always set points to 0
    setEditPoints(isUngraded ? 0 : (question.score || question.points || 10)); // Use score if available, fallback to points
    setEditBloomLevel(question.bloomLevel || '');
    setEditCourseOutcome(question.courseOutcome || '');
  };

  const handleSaveEdit = () => {
    if (!editQuestionText.trim()) {
      alert('Please enter a question');
      return;
    }

    // Only validate if assignment is not ungraded
    if (!isUngraded && totalPoints > 0) {
      const currentQuestion = questions.find(q => q.id === editingQuestionId);
      const currentTotal = calculateTotalQuestionPoints();
      const oldPoints = currentQuestion?.points || 0;
      const newPoints = editPoints || 10;
      const newTotal = currentTotal - oldPoints + newPoints;

      if (newTotal > totalPoints) {
        const remaining = totalPoints - (currentTotal - oldPoints);
        if (remaining <= 0) {
          alert(`Cannot update question. Total points would exceed the limit (${newTotal} > ${totalPoints}). Please adjust the points.`);
          return;
        }
        const confirm = window.confirm(
          `Warning: Updating this question will exceed the total points (${newTotal} > ${totalPoints}).\n\n` +
          `Current total: ${currentTotal} points\n` +
          `Old question points: ${oldPoints} points\n` +
          `New question points: ${newPoints} points\n` +
          `Remaining points available: ${remaining} points\n\n` +
          `Would you like to proceed anyway?`
        );
        if (!confirm) {
          return;
        }
      }
    }

    // For ungraded assignments, always use 0 points
    const finalPoints = isUngraded ? 0 : (editPoints || 10);

    const coCode = extractOutcomeCode(editCourseOutcome) || undefined;
    const coV2Id = resolveV2Id(coCode);
    setQuestions(prev => prev.map(q =>
      q.id === editingQuestionId
        ? {
            ...q,
            question: editQuestionText.trim(),
            points: finalPoints,
            score: finalPoints,
            bloomLevel: editBloomLevel || undefined,
            courseOutcome: coCode,
            ...(coV2Id ? { courseOutcomeId: coV2Id } : {}),
          }
        : q
    ));

    setEditingQuestionId(null);
    setEditQuestionText('');
    setEditPoints(isUngraded ? 0 : 10);
    setEditBloomLevel('');
    setEditCourseOutcome('');
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditQuestionText('');
    setEditPoints(isUngraded ? 0 : 10);
    setEditBloomLevel('');
    setEditCourseOutcome('');
  };

  // Assignment-level attachment handlers with validation
  const handleAttachmentUpload = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);

      // Validate file types according to backend guide
      const allowedTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'application/json'
      ];

      const validFiles = newFiles.filter(file => {
        const isValid = allowedTypes.includes(file.type);
        if (!isValid) {
          console.warn(`File "${file.name}" has invalid type: ${file.type}`);
        }
        return isValid;
      });

      if (validFiles.length !== newFiles.length) {
        alert('Some files have invalid types and were removed. Allowed types: PDF, JPEG, PNG, DOC, DOCX, XLS, XLSX, CSV, JSON');
      }

      // Validate file size (20MB limit - adjust if backend has different limit)
      const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
      const sizeValidFiles = validFiles.filter(file => {
        if (file.size > MAX_FILE_SIZE) {
          alert(`File "${file.name}" is too large. Maximum size: 20MB`);
          return false;
        }
        return true;
      });

      setAttachments((prev) => [...prev, ...sizeValidFiles]);
    }
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  const subjectiveQuestions = questions.filter(q => q.type === 'subjective');
  const objectiveQuestions = questions.filter(q => q.type === 'objective');

  // Auto-create subjective question when attachments are added
  useEffect(() => {
    // Only for subjective assignments
    if (assignmentType !== 'subjective' && activeTab !== 'subjective') {
      return;
    }

    setQuestions(prev => {
      // Check if there's already an auto-generated question from attachments
      const hasAutoAttachmentQuestion = prev.some(
        q => q.type === 'subjective' && q.source === 'attachment-auto'
      );

      if (attachments.length > 0 && !hasAutoAttachmentQuestion) {
        // Create auto-question for attachments
        const autoQuestion = {
          id: `attachment_auto_${Date.now()}`,
          question: 'Solve the given assignment questions in the attached sheet(s).',
          type: 'subjective',
          points: isUngraded ? 0 : totalPoints,
          score: isUngraded ? 0 : totalPoints, // Add score field for per-question scoring
          source: 'attachment-auto',
          status: 'approved'
        };

        return [...prev, autoQuestion];
      } else if (attachments.length === 0 && hasAutoAttachmentQuestion) {
        // Remove auto-question when all attachments are removed
        return prev.filter(q => !(q.type === 'subjective' && q.source === 'attachment-auto'));
      } else if (attachments.length > 0 && hasAutoAttachmentQuestion) {
        // Update points and score of auto-question when totalPoints changes
        return prev.map(q =>
          q.source === 'attachment-auto' && q.type === 'subjective'
            ? { ...q, points: isUngraded ? 0 : totalPoints, score: isUngraded ? 0 : totalPoints }
            : q
        );
      }

      return prev;
    });
  }, [attachments.length, assignmentType, activeTab, isUngraded, totalPoints, setQuestions]);

  // If assignmentType is restricted, ensure activeTab matches and filter questions
  useEffect(() => {
    if (assignmentType) {
      setActiveTab(assignmentType);
      // Filter out questions that don't match the assignment type
      setQuestions(prev => {
        const validQuestions = prev.filter(q => q.type === assignmentType);
        // Only update if there are invalid questions
        if (validQuestions.length !== prev.length) {
          return validQuestions;
        }
        return prev;
      });
    }
  }, [assignmentType, setActiveTab, setQuestions]);

  return (
    <motion.div {...motionFadeUp}>
      {/* ── Type toggle — only when both types allowed ─────── */}
      {!assignmentType && (
        <div className="flex items-center gap-1 mb-5">
          {[
            { key: 'subjective', icon: FileText, label: 'Subjective', count: subjectiveQuestions.length },
            { key: 'objective', icon: CheckCircle2, label: 'Objective', count: objectiveQuestions.length }
          ].map(tab => {
            const isOn = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  "relative flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors",
                  isOn
                    ? "text-accent1"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                )}
              >
                <tab.icon className="h-3.5 w-3.5" />
                {tab.label}
                <span className={cn(
                  "ml-1 px-1.5 py-0.5 text-xs rounded-sm font-bold tabular-nums",
                  isOn ? "bg-accent1/10 text-accent1" : "bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
                )}>
                  {tab.count}
                </span>
                {isOn && (
                  <motion.div
                    layoutId="questionsTypeTab"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent1"
                    transition={{ duration: 0.2 }}
                  />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Points summary bar ─────────────────────────────── */}
      {!isUngraded && totalPoints > 0 && (
        <div className="mb-5">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Points
            </span>
            <span className={cn(
              "text-xs font-bold tabular-nums",
              calculateTotalQuestionPoints() === totalPoints
                ? 'text-emerald-600 dark:text-emerald-400'
                : calculateTotalQuestionPoints() > totalPoints
                ? 'text-red-600 dark:text-red-400'
                : 'text-gray-600 dark:text-gray-300'
            )}>
              {calculateTotalQuestionPoints()} / {totalPoints}
            </span>
          </div>
          <div className="h-1.5 bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden">
            <div
              className={cn(
                "h-full rounded-sm transition-all duration-500",
                calculateTotalQuestionPoints() === totalPoints
                  ? 'bg-emerald-500'
                  : calculateTotalQuestionPoints() > totalPoints
                  ? 'bg-red-500'
                  : 'bg-accent1'
              )}
              style={{ width: `${Math.min((calculateTotalQuestionPoints() / totalPoints) * 100, 100)}%` }}
            />
          </div>
          {calculateTotalQuestionPoints() !== totalPoints && (
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
              {calculateTotalQuestionPoints() > totalPoints
                ? `Exceeds by ${calculateTotalQuestionPoints() - totalPoints} pts`
                : `${totalPoints - calculateTotalQuestionPoints()} pts remaining`
              }
            </p>
          )}
        </div>
      )}

      {/* ── Tab Content ────────────────────────────────────── */}
      <div className="min-h-[400px]">
        {(activeTab === 'subjective' || assignmentType === 'subjective') && assignmentType !== 'objective' && (
          <div className="space-y-4">

            {/* Sub-tabs: Create / Attachments */}
            <div className="flex items-center gap-1 border-b border-gray-200 dark:border-gray-700">
              {[
                { key: 'create', label: 'Create Questions' },
                { key: 'attachments', label: `Attachments${attachments.length > 0 ? ` (${attachments.length})` : ''}` },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setQuestionsTab(tab.key)}
                  className={cn(
                    "relative px-4 py-2.5 text-sm font-medium transition-colors",
                    questionsTab === tab.key
                      ? "text-accent1"
                      : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  )}
                >
                  {tab.label}
                  {questionsTab === tab.key && (
                    <motion.div
                      layoutId="questionsSubTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent1"
                      transition={{ duration: 0.2 }}
                    />
                  )}
                </button>
              ))}
            </div>

            {/* Create tab */}
            {questionsTab === 'create' && (
              <div className="space-y-4">
                {/* Action buttons — compact row */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowAddQuestionModal(true)}
                    className={cn(btnPrimary, "flex-1 py-2.5")}
                  >
                    <Plus size={16} />
                    Add Question
                  </button>
                  <button
                    onClick={() => setShowAIGenerationModal(true)}
                    className={cn(btnSecondary, "flex-1 py-2.5")}
                  >
                    <Sparkles size={16} />
                    Generate with AI
                  </button>
                </div>

                {/* Question list */}
                {subjectiveQuestions.length === 0 ? (
                  <div className={cn(emptyState)}>
                    <FileText className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-3")} />
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No questions yet</p>
                    <p className={bodySecondary}>Add manually or generate with AI</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {subjectiveQuestions.map((q, index) => (
                      <motion.div
                        key={q.id}
                        {...motionStaggerChild(index)}
                      >
                        {editingQuestionId === q.id ? (
                          /* ── Inline edit form ──────────────── */
                          <div className={cn(card, "p-4 space-y-3")}>
                            <textarea
                              value={editQuestionText}
                              onChange={(e) => setEditQuestionText(e.target.value)}
                              className={inputClasses}
                              rows="3"
                              placeholder="Question text..."
                            />
                            <div className="grid grid-cols-3 gap-3">
                              <input
                                type="number"
                                value={editPoints}
                                onChange={(e) => !isUngraded && setEditPoints(Number(e.target.value))}
                                min="0"
                                disabled={isUngraded}
                                placeholder="Points"
                                className={cn(inputClasses, isUngraded && 'opacity-50 cursor-not-allowed')}
                              />
                              <select
                                value={editBloomLevel}
                                onChange={(e) => setEditBloomLevel(e.target.value)}
                                className={inputClasses}
                              >
                                <option value="">Bloom Level</option>
                                {bloomLevels.map(level => (
                                  <option key={level} value={level.toLowerCase()}>{level}</option>
                                ))}
                              </select>
                              <select
                                value={editCourseOutcome}
                                onChange={(e) => setEditCourseOutcome(e.target.value)}
                                className={inputClasses}
                              >
                                <option value="">Course Outcome</option>
                                {getCourseOutcomes().map((outcome, idx) => (
                                  <option key={idx} value={extractOutcomeCode(outcome)}>
                                    {formatOutcomeForDisplay(outcome)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex justify-end gap-2">
                              <button onClick={handleCancelEdit} className={btnSecondary}>Cancel</button>
                              <button onClick={handleSaveEdit} className={btnPrimary}>
                                <Save size={14} /> Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* ── Question row ──────────────────── */
                          <div className={cn(card, "flex items-start gap-3 p-3 sm:p-4 group/q")}>
                            {/* Number */}
                            <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-sm bg-accent1/10 text-accent1 text-xs font-bold">
                              {index + 1}
                            </span>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-gray-900 dark:text-white leading-relaxed line-clamp-2">{q.question}</p>
                              <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                {q.source === 'ai' && (
                                  <span className={cn(badgeBase, "bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700 text-[10px] flex items-center gap-1")}>
                                    <Sparkles size={9} /> AI
                                  </span>
                                )}
                                {q.source === 'attachment-auto' && (
                                  <span className={cn(badgeBase, "bg-accent1/10 text-accent1 border-accent1/20 text-[10px] flex items-center gap-1")}>
                                    <Paperclip size={9} /> Auto
                                  </span>
                                )}
                                {q.bloomLevel && (
                                  <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 text-[10px]")}>
                                    {q.bloomLevel}
                                  </span>
                                )}
                                {q.courseOutcome && (
                                  <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 text-[10px]")}>
                                    {q.courseOutcome}
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Points + Actions */}
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {!isUngraded && q.points > 0 && (
                                <span className="text-xs font-bold text-accent1 tabular-nums">
                                  {q.points} pts
                                </span>
                              )}
                              <button
                                onClick={() => handleStartEdit(q)}
                                className="p-1.5 rounded-sm text-gray-400 hover:text-accent1 hover:bg-accent1/10 opacity-0 group-hover/q:opacity-100 transition-all"
                                title="Edit"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => removeQuestion(q.id)}
                                className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover/q:opacity-100 transition-all"
                                title="Delete"
                              >
                                <Trash size={14} />
                              </button>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Attachments Tab Content */}
            {questionsTab === 'attachments' && (
              <div className={cn(card, "p-6")}>
                <div className="flex items-center gap-2 mb-4">
                  <Paperclip className="text-accent1" size={20} />
                  <h3 className={cardTitle}>Assignment Attachments</h3>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Files
                    </label>
                    <div className={cn(emptyState, "hover:border-accent1 dark:hover:border-accent1 transition-colors")}>
                      <input
                        type="file"
                        onChange={handleAttachmentUpload}
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.json,image/jpeg,image/png"
                        className="hidden"
                        id="assignment-attachment-upload"
                      />
                      <label
                        htmlFor="assignment-attachment-upload"
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Upload className={cn(iconSize.empty, "text-gray-400 dark:text-gray-500")} />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Click to upload files</span>
                        <span className={bodySecondary}>PDF, DOC, DOCX, Images, etc.</span>
                      </label>
                    </div>
                  </div>

                  {attachments.length > 0 && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Attached Files ({attachments.length})
                      </label>
                      <div className="space-y-2">
                        {attachments.map((file, index) => (
                          <div
                            key={index}
                            className={cn(surfaceMuted, "rounded-sm border", dividerInner, "flex items-center justify-between p-3")}
                          >
                            <div className="flex items-center gap-2">
                              <Paperclip className="text-gray-500 dark:text-gray-400" size={16} />
                              <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                              <span className={bodySecondary}>
                                ({(file.size / 1024).toFixed(2)} KB)
                              </span>
                            </div>
                            <button
                              onClick={() => handleRemoveAttachment(index)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Remove attachment"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {attachments.length === 0 && (
                    <div className={cn(emptyState)}>
                      <Paperclip className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-2")} />
                      <p className={bodySecondary}>No attachments added yet</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Objective tab ────────────────────────────────── */}
        {(activeTab === 'objective' || assignmentType === 'objective') && assignmentType !== 'subjective' && (
          <div className="space-y-4">

            {/* Action buttons — compact row */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.csv,.json';
                  input.onchange = handleFileUpload;
                  input.click();
                }}
                className={cn(btnSecondary, "flex-1 py-2.5")}
              >
                <Upload size={16} />
                Upload CSV/JSON
              </button>
              <button
                onClick={() => setShowMCQGenerator(true)}
                className={cn(btnPrimary, "flex-1 py-2.5")}
              >
                <Plus size={16} />
                MCQ Generator
              </button>
              <button
                onClick={() => setShowAIGenerationModalMCQ(true)}
                className={cn(btnSecondary, "flex-1 py-2.5")}
              >
                <Sparkles size={16} />
                Generate with AI
              </button>
            </div>

            {/* Format help — collapsible */}
            <details className={cn(card, "group")}>
              <summary className="flex items-center gap-2 px-4 py-2.5 cursor-pointer text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-accent1 transition-colors">
                <HelpCircle size={14} />
                CSV / JSON format guide
              </summary>
              <div className="px-4 pb-4 space-y-3">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">CSV Header</p>
                  <code className="block text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm p-2 text-gray-700 dark:text-gray-300 overflow-x-auto">
                    question,type,option_a,option_b,option_c,option_d,correct_answer,points,bloom_level,course_outcome
                  </code>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1.5">JSON</p>
                  <code className="block text-[11px] bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm p-2 text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre">
{`[{ "question": "...", "type": "objective", "options": ["A","B","C","D"], "correctAnswer": "A", "points": 10 }]`}
                  </code>
                </div>
              </div>
            </details>

            {/* Question list */}
            {objectiveQuestions.length === 0 ? (
              <div className={cn(emptyState)}>
                <CheckCircle2 className={cn(iconSize.empty, "mx-auto text-gray-400 dark:text-gray-500 mb-3")} />
                <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No objective questions yet</p>
                <p className={bodySecondary}>Upload a file, use MCQ generator, or generate with AI</p>
              </div>
            ) : (
              <div className="space-y-2">
                {objectiveQuestions.map((q, index) => (
                  <motion.div
                    key={q.id}
                    {...motionStaggerChild(index)}
                  >
                    {editingQuestionId === q.id ? (
                      /* ── Inline edit form ──────────────── */
                      <div className={cn(card, "p-4 space-y-3")}>
                        <textarea
                          value={editQuestionText}
                          onChange={(e) => setEditQuestionText(e.target.value)}
                          className={inputClasses}
                          rows="3"
                          placeholder="Question text..."
                        />
                        <div className="grid grid-cols-3 gap-3">
                          <input
                            type="number"
                            value={editPoints}
                            onChange={(e) => !isUngraded && setEditPoints(Number(e.target.value))}
                            min="0"
                            disabled={isUngraded}
                            placeholder="Points"
                            className={cn(inputClasses, isUngraded && 'opacity-50 cursor-not-allowed')}
                          />
                          <select
                            value={editBloomLevel}
                            onChange={(e) => setEditBloomLevel(e.target.value)}
                            className={inputClasses}
                          >
                            <option value="">Bloom Level</option>
                            {bloomLevels.map(level => (
                              <option key={level} value={level.toLowerCase()}>{level}</option>
                            ))}
                          </select>
                          <select
                            value={editCourseOutcome}
                            onChange={(e) => setEditCourseOutcome(e.target.value)}
                            className={inputClasses}
                          >
                            <option value="">Course Outcome</option>
                            {getCourseOutcomes().map((outcome, idx) => (
                              <option key={idx} value={extractOutcomeCode(outcome)}>
                                {formatOutcomeForDisplay(outcome)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex justify-end gap-2">
                          <button onClick={handleCancelEdit} className={btnSecondary}>Cancel</button>
                          <button onClick={handleSaveEdit} className={btnPrimary}>
                            <Save size={14} /> Save
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── MCQ question row ──────────────── */
                      <div className={cn(card, "p-3 sm:p-4 group/q")}>
                        <div className="flex items-start gap-3">
                          {/* Number */}
                          <span className="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded-sm bg-accent1/10 text-accent1 text-xs font-bold">
                            {index + 1}
                          </span>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-900 dark:text-white leading-relaxed mb-2">{q.question}</p>

                            {/* Options grid */}
                            {q.options && (
                              <div className="grid grid-cols-2 gap-1.5 mb-2">
                                {q.options.map((option, optIndex) => (
                                  <div
                                    key={optIndex}
                                    className={cn(
                                      "px-2.5 py-1.5 rounded-sm border text-xs",
                                      q.correctAnswer === option
                                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 font-medium'
                                        : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
                                    )}
                                  >
                                    <span className="font-bold mr-1.5">{String.fromCharCode(65 + optIndex)}.</span>
                                    {option}
                                    {q.correctAnswer === option && (
                                      <CheckCircle2 className="inline ml-1" size={11} />
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Badges */}
                            <div className="flex flex-wrap items-center gap-1.5">
                              {q.source === 'ai' && (
                                <span className={cn(badgeBase, "bg-yellow-50 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 border-yellow-200 dark:border-yellow-700 text-[10px] flex items-center gap-1")}>
                                  <Sparkles size={9} /> AI
                                </span>
                              )}
                              {q.bloomLevel && (
                                <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 text-[10px]")}>
                                  {q.bloomLevel}
                                </span>
                              )}
                              {q.courseOutcome && (
                                <span className={cn(badgeBase, "bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600 text-[10px]")}>
                                  {q.courseOutcome}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Points + Actions */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!isUngraded && q.points > 0 && (
                              <span className="text-xs font-bold text-accent1 tabular-nums">
                                {q.points} pts
                              </span>
                            )}
                            <button
                              onClick={() => handleStartEdit(q)}
                              className="p-1.5 rounded-sm text-gray-400 hover:text-accent1 hover:bg-accent1/10 opacity-0 group-hover/q:opacity-100 transition-all"
                              title="Edit"
                            >
                              <Edit size={14} />
                            </button>
                            <button
                              onClick={() => removeQuestion(q.id)}
                              className="p-1.5 rounded-sm text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover/q:opacity-100 transition-all"
                              title="Delete"
                            >
                              <Trash size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showMCQGenerator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(card, "max-w-4xl w-full max-h-[90vh] overflow-auto")}
          >
            <MCQGenerator
              onSave={(generatedQuestions) => {
                const validQuestions = assignmentType
                  ? generatedQuestions.filter(q => q.type === assignmentType)
                  : generatedQuestions;

                if (assignmentType && validQuestions.length < generatedQuestions.length) {
                  alert(`Only ${assignmentType} questions are allowed. Some questions were filtered out.`);
                }

                if (!isUngraded && totalPoints > 0) {
                  const currentTotal = calculateTotalQuestionPoints();
                  const newQuestionsTotal = validQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
                  const newTotal = currentTotal + newQuestionsTotal;

                  if (newTotal > totalPoints) {
                    const remaining = totalPoints - currentTotal;
                    if (remaining <= 0) {
                      alert(`Cannot add questions. Total points already reached (${currentTotal}/${totalPoints}).`);
                      return;
                    }
                    const confirm = window.confirm(
                      `Adding these questions will exceed total points (${newTotal} > ${totalPoints}). Proceed?`
                    );
                    if (!confirm) return;
                  }
                }

                const enrichedMCQ = validQuestions.map(q => ({
                  ...q,
                  ...(q.courseOutcome && !q.courseOutcomeId ? { courseOutcomeId: resolveV2Id(q.courseOutcome) } : {}),
                }));
                setQuestions(prev => [...prev, ...enrichedMCQ]);
                setShowMCQGenerator(false);
              }}
              onCancel={() => setShowMCQGenerator(false)}
              courseData={courseData}
              bloomLevels={bloomLevels}
              getCourseOutcomes={getCourseOutcomes}
              formatOutcomeForDisplay={formatOutcomeForDisplay}
              extractOutcomeCode={extractOutcomeCode}
            />
          </motion.div>
        </div>
      )}

      <AIApprovalModal
        isOpen={showAIApproval}
        onClose={() => setShowAIApproval(false)}
        questions={pendingAIQuestions}
        onApprove={handleApproveAIQuestions}
        onReject={handleRejectAIQuestions}
      />

      <AddQuestionModal
        isOpen={showAddQuestionModal}
        onClose={() => setShowAddQuestionModal(false)}
        onSave={handleAddManualQuestion}
        courseData={courseData}
        bloomLevels={bloomLevels}
        getCourseOutcomes={getCourseOutcomes}
        formatOutcomeForDisplay={formatOutcomeForDisplay}
        extractOutcomeCode={extractOutcomeCode}
        resolveV2Id={resolveV2Id}
        totalPoints={totalPoints}
        isUngraded={isUngraded}
        currentTotalPoints={calculateTotalQuestionPoints()}
      />

      <AIGenerationModal
        isOpen={showAIGenerationModal}
        onClose={() => {
          setShowAIGenerationModal(false);
          setAiError(null);
        }}
        onGenerate={generateAISubjectiveQuestions}
        courseData={courseData}
        bloomLevels={bloomLevels}
        getCourseOutcomes={getCourseOutcomes}
        formatOutcomeForDisplay={formatOutcomeForDisplay}
        extractOutcomeCode={extractOutcomeCode}
        generating={generating}
        aiError={aiError}
      />

      <AIGenerationModal
        isOpen={showAIGenerationModalMCQ}
        onClose={() => {
          setShowAIGenerationModalMCQ(false);
          setAiError(null);
        }}
        onGenerate={generateAIMCQQuestions}
        courseData={courseData}
        bloomLevels={bloomLevels}
        getCourseOutcomes={getCourseOutcomes}
        formatOutcomeForDisplay={formatOutcomeForDisplay}
        extractOutcomeCode={extractOutcomeCode}
        generating={generating}
        aiError={aiError}
      />
    </motion.div>
  );
};

export default QuestionsStep;
